import { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * SageParticleOrb — living, particle-based visual representation of SAGE.
 *
 * Sits inside the audio orb on /live-coaching. Same box + size as the
 * previous static Sparkles icon.
 *
 * States (driven by props from LiveCoaching):
 *   idle       → gentle radial drift, low-amplitude breathing
 *   listening  → particles ease INWARD toward center (user is speaking)
 *   speaking   → particles pulse OUTWARD in soft waves; when an
 *                `audioAnalyser` is provided the wave is TETHERED to
 *                SAGE's actual voice (per-frame frequency amplitude),
 *                so louder syllables → wider expansion, silences →
 *                gentle collapse.
 *
 * Design choices this iteration (per user feedback):
 *   • Particle size trimmed ~2× — the swarm now reads as dust motes
 *     rather than large glowing dots (base 0.25 + rand 0.9, was 0.6 +
 *     rand 2.4).
 *   • Gradient falloff tightened (size * 1.8 vs 2.2) — smaller particles
 *     stay crisp without smearing into each other under `lighter` blend.
 *   • Audio-tethered mode: analyser feeds a smoothed amplitude signal
 *     that modulates radial offset AND per-particle alpha, so the swarm
 *     visibly breathes with speech rather than a synthesized pulse.
 *   • Particles also carry a per-particle phase offset that shows up
 *     only during speaking — like each mote is catching a different
 *     frequency.
 *
 * Perf:
 *   • Canvas 2D. Single RAF loop. Particles allocated once at mount.
 *   • DPR-aware. ResizeObserver for the parent box.
 *   • `prefers-reduced-motion` → single static frame, no loop.
 *   • Analyser is READ ONLY — the actual audio graph is owned by
 *     LiveCoaching, we just borrow the AnalyserNode ref.
 */

const DEFAULT_PARTICLE_COUNT = 195;

// Warm cream/amber palette — the native SAGE palette. On a dark backdrop
// these dust-coloured particles ADD light (composite='lighter') and read
// as warm embers drifting through fog.
const PALETTE_DARK = [
  [255, 244, 214], // warm cream
  [252, 231, 178], // pale gold
  [244, 212, 122], // Tailwind yellow-300 (matches tutorial spotlight)
  [230, 200, 160], // soft amber
];

// Light-mode palette — SAME warm family, but shifted DEEP so the
// particles DARKEN the linen background instead of trying (and failing)
// to add brightness to it. Composite also switches to 'source-over'
// in light mode so a dark amber dot stays a dark amber dot on linen
// rather than blowing out to a muddy highlight. The swarm reads as
// honeyed dust catching afternoon light.
const PALETTE_LIGHT = [
  [120, 72, 20],   // deep umber-amber
  [148, 98, 32],   // burnt honey
  [168, 118, 48],  // old gold
  [96, 60, 24],    // bronze
];

const pickColor = (palette) => palette[Math.floor(Math.random() * palette.length)];

const spawnParticle = (baseRadius, palette) => {
  const angle = Math.random() * Math.PI * 2;
  const radialSpread = baseRadius * 0.45;
  const r = baseRadius + (Math.random() - 0.5) * radialSpread;
  // Smaller particles per user preference — reads as dust, not orbs.
  const size = 0.25 + Math.random() * 0.9;
  return {
    angle,
    radius: r,
    baseRadius: r,
    // Angular velocity — slow, mixed CW/CCW, so no synced rotation.
    dAngle: (Math.random() - 0.5) * 0.0035,
    // Idle breathing offset — everyone drifts at their own pace.
    radialPhase: Math.random() * Math.PI * 2,
    radialSpeed: 0.008 + Math.random() * 0.014,
    radialAmp: 3 + Math.random() * 6,
    // Per-particle "voice tether" phase used only in speaking state. The
    // particle's response to the audio amplitude is offset by this
    // phase so the swarm shimmers rather than pumping in lockstep.
    voicePhase: Math.random() * Math.PI * 2,
    voiceSensitivity: 0.6 + Math.random() * 0.9, // 0.6..1.5
    size,
    baseAlpha: 0.32 + Math.random() * 0.5,
    color: pickColor(palette),
  };
};

const SageParticleOrb = ({
  state = 'idle', // 'idle' | 'listening' | 'speaking'
  particleCount = DEFAULT_PARTICLE_COUNT,
  audioAnalyser = null, // optional { current: AnalyserNode } — see props docs
  className = '',
  testid = 'sage-particle-orb',
}) => {
  const { theme } = useTheme();
  // Keep a ref so the RAF loop reads the latest theme without
  // re-mounting the canvas on every toggle. Theme changes also trigger
  // a cheap re-seed of the particle colours (see effect below).
  const themeRef = useRef(theme);
  useEffect(() => { themeRef.current = theme; }, [theme]);
  // Re-colour the existing particles when the user flips theme so the
  // swarm transitions smoothly instead of popping. We don't re-spawn —
  // positions and velocities carry over, only `color` is swapped.
  useEffect(() => {
    const palette = theme === 'light' ? PALETTE_LIGHT : PALETTE_DARK;
    const ps = particlesRef.current;
    if (!ps || !ps.length) return;
    for (let i = 0; i < ps.length; i += 1) {
      ps[i].color = pickColor(palette);
    }
  }, [theme]);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const stateRef = useRef(state);
  const dprRef = useRef(1);
  const sizeRef = useRef({ w: 0, h: 0 });
  const analyserPropRef = useRef(audioAnalyser);
  // Smoothed amplitude (0..1). Persists across frames so a silent frame
  // between syllables doesn't collapse the swarm instantly.
  const smoothedAmpRef = useRef(0);
  // Frequency-buffer reused each frame — no per-frame allocation.
  const freqBufRef = useRef(null);

  // Keep the latest state visible to the RAF loop without re-mounting.
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    analyserPropRef.current = audioAnalyser;
    // Reallocate the frequency buffer if the analyser has a new size.
    if (audioAnalyser?.current?.frequencyBinCount) {
      freqBufRef.current = new Uint8Array(audioAnalyser.current.frequencyBinCount);
    }
  }, [audioAnalyser]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      dprRef.current = dpr;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      sizeRef.current = { w: rect.width, h: rect.height };
      const baseRadius = Math.min(rect.width, rect.height) * 0.35;
      const palette = themeRef.current === 'light' ? PALETTE_LIGHT : PALETTE_DARK;
      particlesRef.current = Array.from({ length: particleCount }, () =>
        spawnParticle(baseRadius, palette),
      );
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      // Single static frame — respects the user preference.
      const { w, h } = sizeRef.current;
      const dpr = dprRef.current;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';
      particlesRef.current.forEach((p) => {
        const cx = w / 2 + Math.cos(p.angle) * p.radius;
        const cy = h / 2 + Math.sin(p.angle) * p.radius;
        drawParticle(ctx, cx * dpr, cy * dpr, p.size * dpr, p.color, p.baseAlpha);
      });
      return () => ro.disconnect();
    }

    /**
     * Pull a normalized amplitude signal (0..1) from the AnalyserNode.
     * We only average the low-mid band (~80–1500 Hz) which is where
     * speech energy concentrates — using the full spectrum makes ambient
     * hiss dominate. Falls back to 0 when no analyser is attached; the
     * caller will then use a synthesized pulse.
     */
    const readAmplitude = () => {
      const analyser = analyserPropRef.current?.current;
      if (!analyser || !freqBufRef.current) return null;
      const buf = freqBufRef.current;
      analyser.getByteFrequencyData(buf);
      // Sample the first ~30% of bins (speech band for typical 44.1 kHz
      // FFT sizes 256..1024). Cheap and effective.
      const end = Math.max(4, Math.floor(buf.length * 0.3));
      let sum = 0;
      for (let i = 0; i < end; i += 1) sum += buf[i];
      const avg = sum / end / 255; // 0..1
      return avg;
    };

    let t = 0;
    const step = () => {
      t += 1;
      const { w, h } = sizeRef.current;
      const dpr = dprRef.current;
      const cx0 = w / 2;
      const cy0 = h / 2;

      // Motion trail via destination-out fade instead of full clear.
      // Composite mode and fade colour differ by theme: in dark mode
      // we use additive 'lighter' so warm cream dots glow against
      // obsidian; in light mode we switch to standard 'source-over'
      // with a linen-coloured fade so dark amber dots darken the
      // linen base (otherwise additive on a cream background washes
      // the particles into invisibility).
      const isLight = themeRef.current === 'light';
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = isLight
        ? 'rgba(245, 239, 227, 0.18)'  // linen alpha-erase
        : 'rgba(0, 0, 0, 0.14)';       // obsidian alpha-erase
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.globalCompositeOperation = isLight ? 'source-over' : 'lighter';

      // Update the smoothed amplitude signal.
      //
      // In speaking state:
      //   If an analyser is bound, read the real voice band and ease
      //   the smoothed value toward it (attack fast, release slow — same
      //   shape a VU meter uses; feels responsive without twitchy noise).
      //   If no analyser, synthesize a smooth pulse so speaking still
      //   looks alive.
      // In other states: decay the signal toward zero.
      if (stateRef.current === 'speaking') {
        const raw = readAmplitude();
        if (raw !== null) {
          // Attack (fast) vs release (slow) → punchy on syllable onset,
          // gracefully trails off on decay.
          const target = raw;
          const attack = 0.35;
          const release = 0.08;
          const rate = target > smoothedAmpRef.current ? attack : release;
          smoothedAmpRef.current +=
            (target - smoothedAmpRef.current) * rate;
        } else {
          // Synthesized fallback — smooth sinusoidal pulse.
          const synth = Math.sin(t * 0.06) * 0.5 + 0.5;
          smoothedAmpRef.current += (synth * 0.6 - smoothedAmpRef.current) * 0.08;
        }
      } else {
        smoothedAmpRef.current *= 0.92;
      }

      const amp = smoothedAmpRef.current; // 0..1-ish

      particlesRef.current.forEach((p) => {
        p.angle += p.dAngle;

        const breath = Math.sin(t * p.radialSpeed + p.radialPhase) * p.radialAmp;

        // State-dependent radial target.
        let stateOffset = 0;
        let stateScale = 1;
        if (stateRef.current === 'listening') {
          stateScale = 0.75;
        } else if (stateRef.current === 'speaking') {
          // Per-particle phase offset means each dust mote responds
          // slightly out of sync — like different phonemes catching
          // different motes at slightly different moments.
          const localPhase = Math.sin(t * 0.09 + p.voicePhase);
          stateOffset = amp * 22 * p.voiceSensitivity * (0.55 + 0.45 * localPhase);
          stateScale = 1.04;
        }

        const targetR = p.baseRadius * stateScale + breath + stateOffset;
        // Ease toward target — 0.09 for speaking (a touch snappier so
        // words land visibly), 0.06 otherwise.
        const ease = stateRef.current === 'speaking' ? 0.09 : 0.06;
        p.radius += (targetR - p.radius) * ease;

        const x = cx0 + Math.cos(p.angle) * p.radius;
        const y = cy0 + Math.sin(p.angle) * p.radius;

        // Alpha modulation. When speaking, each particle's baseline
        // brightness is lifted by the amp AND by its own phase — makes
        // the swarm shimmer with the voice, not just expand with it.
        let alpha = p.baseAlpha;
        if (stateRef.current === 'speaking') {
          const localTwinkle = 0.5 + 0.5 * Math.sin(t * 0.12 + p.voicePhase * 2);
          alpha = Math.min(1, p.baseAlpha + amp * 0.4 * localTwinkle);
        } else if (stateRef.current === 'listening') {
          alpha = p.baseAlpha * 0.85;
        }

        drawParticle(ctx, x * dpr, y * dpr, p.size * dpr, p.color, alpha);
      });

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [particleCount]);

  return (
    <canvas
      ref={canvasRef}
      className={`w-full h-full block ${className}`}
      data-testid={testid}
      data-state={state}
      data-audio-tethered={audioAnalyser ? 'true' : 'false'}
      aria-hidden="true"
    />
  );
};

/**
 * Paint one soft radial-gradient particle. Tighter falloff (size × 1.8)
 * than the previous version (× 2.2) — with the smaller particle sizes,
 * a narrower gradient keeps them reading as crisp dust motes.
 */
const drawParticle = (ctx, x, y, size, [r, g, b], alpha) => {
  const halo = size * 1.8;
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, halo);
  gradient.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
  gradient.addColorStop(0.55, `rgba(${r},${g},${b},${alpha * 0.32})`);
  gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, halo, 0, Math.PI * 2);
  ctx.fill();
};

export default SageParticleOrb;
